// The review meter's breakdown: per changed file, the lines reviewed and
// in all, and the changes still unreviewed, each with the lines the Files
// view opens it at.
import type { Block } from '../engine/types';
import type { Coverage } from './partial';
import { LINE_PARAM, formatLines } from './snapshotLink';
import { COMMENT_PARAM, THREAD_PARAM } from './headerTargets';

export interface UnreviewedChange {
  /** The block's key. */
  block: string;
  /** Its enclosing symbol, or '' when the engine names none. */
  title: string;
  /** The side and lines of its unreviewed rows: the new side when it has any there, else the old. */
  side: 'LEFT' | 'RIGHT';
  start: number;
  end: number;
  /** Unreviewed removed and added lines. */
  lines: number;
}

export interface FileProgress {
  path: string;
  done: number;
  total: number;
  /** False until the file's diff and coverage are computed: `total` is then GitHub's count and `changes` is empty. */
  exact: boolean;
  changes: UnreviewedChange[];
}

/** A change block's unreviewed rows, as the place to open it; null when every changed row is covered. */
function unreviewed(b: Block, c: Coverage | undefined): UnreviewedChange | null {
  const left: number[] = [];
  const right: number[] = [];
  b.rows.forEach((r, j) => {
    if (r.kind === 'context' || c?.covered[j]) return;
    if (r.kind === 'add' && r.new !== null) right.push(r.new);
    if (r.kind === 'del' && r.old !== null) left.push(r.old);
  });
  const lines = left.length + right.length;
  if (!lines) return null;
  const [side, at] = right.length ? (['RIGHT', right] as const) : (['LEFT', left] as const);
  return { block: b.key, title: b.title, side, start: Math.min(...at), end: Math.max(...at), lines };
}

/**
 * The breakdown of `paths` (those `pass` keeps, all when omitted): each
 * file's progress (`progress`, as the meter counts it) and its unreviewed
 * changes (from its blocks and coverage, when computed), files with
 * unreviewed lines first, otherwise in `paths` order; and the totals.
 */
export function progressBreakdown(
  paths: readonly string[],
  progress: (path: string) => { done: number; total: number; exact: boolean },
  blocksOf: (path: string) => Block[] | null,
  coverageOf: (path: string) => Coverage[] | null,
  pass?: (path: string) => boolean,
): { done: number; total: number; filtered: boolean; files: FileProgress[] } {
  const files: FileProgress[] = [];
  let done = 0;
  let total = 0;
  for (const path of paths) {
    if (pass && !pass(path)) continue;
    const p = progress(path);
    done += p.done;
    total += p.total;
    const blocks = p.exact ? blocksOf(path) : null;
    const cov = coverageOf(path) ?? [];
    const changes = (blocks ?? []).flatMap((b, i) => (b.kind === 'change' ? (unreviewed(b, cov[i]) ?? []) : []));
    files.push({ path, done: p.done, total: p.total, exact: p.exact, changes });
  }
  const open = (f: FileProgress) => (f.done < f.total ? 0 : 1);
  files.sort((a, b) => open(a) - open(b));
  return { done, total, filtered: !!pass, files };
}

/** The query for the Files view at `path`: the reader's comparison and filters kept, no thread or lines from before. */
function at(path: string, query: URLSearchParams): URLSearchParams {
  const q = new URLSearchParams(query);
  for (const k of ['file', LINE_PARAM, THREAD_PARAM, COMMENT_PARAM]) q.delete(k);
  q.set('file', path);
  return q;
}

/** The Files view (`files`, its path) at file `path`. */
export function fileHref(files: string, path: string, query: URLSearchParams): string {
  return `${files}?${at(path, query)}`;
}

/** The Files view at change `c` of file `path`: its lines scrolled to and selected. */
export function changeHref(files: string, path: string, c: Pick<UnreviewedChange, 'side' | 'start' | 'end'>, query: URLSearchParams): string {
  const q = at(path, query);
  q.set(LINE_PARAM, formatLines(c));
  return `${files}?${q}`;
}
